import { Injectable } from '@angular/core';
import { forkJoin, map, Observable, of } from 'rxjs';
import { SharedQueries } from 'src/app/shared/types/get-query';
import { Selectable } from 'src/app/shared/types/selectable.type';
import { IFilterListService } from './ifilter-list.service';

export type CompanyFilterItem = {
	id: number;
	name: string;
};

@Injectable({
	providedIn: 'root'
})
export class CompanyFilterService extends IFilterListService<CompanyFilterItem> {
	private readonly PAGE_SIZE = 50;

	private currentSearch = '';
	private hasNextPage = true;
	private currentCursor: string | null = null;
	private internalDatasource: Selectable<CompanyFilterItem>[] = [];

	private reset(): void {
		this.hasNextPage = true;
		this.currentCursor = null;
		this.internalDatasource = [];
		this.datasource.next([]);
	}

	search(searchText: string, variables?: any, append: boolean = false): void {
		if (this.loading) return;
		if (append && !this.hasNextPage) return;

		if (!append) {
			this.reset();
			this.currentSearch = searchText;
		}

		this.loading = true;
		this.graphQlService
			.getData<any>(SharedQueries.GET_COMPANIES, {
				search: this.currentSearch,
				first: this.PAGE_SIZE,
				after: append ? this.currentCursor : null,
				...variables
			})
			.pipe(
				map((res: any) => {
					const companies = res.data.companiesByUserProfile;
					const items: Selectable<CompanyFilterItem>[] = companies.nodes.map((node: any) => ({
						id: node.id,
						name: node.name,
						displayId: node.id,
						displayName: node.name,
						selected: false,
						disabled: false
					}));

					return { items, pageInfo: companies.pageInfo };
				})
			)
			.subscribe({
				next: ({ items, pageInfo }) => {
					const unique = items.filter((item) => !this.internalDatasource.some((el) => el.displayId === item.displayId));
					this.internalDatasource = [...this.internalDatasource, ...unique];
					this.datasource.next(this.internalDatasource);
					this.currentCursor = pageInfo?.endCursor ?? null;
					this.hasNextPage = !!pageInfo?.hasNextPage;
					this.loading = false;
				},
				error: () => {
					this.loading = false;
				}
			});
	}

	loadNextPage(): void {
		this.search(this.currentSearch, {}, true);
	}

	searchById(_id: string[] | string): Observable<CompanyFilterItem[]> {
		const ids = (Array.isArray(_id) ? _id : [_id]).map((id) => Number(id)).filter((id) => Number.isFinite(id));
		if (!ids.length) return of([]);

		return forkJoin(
			ids.map((id) =>
				this.graphQlService.getData<any>(SharedQueries.GET_COMPANY_BY_ID, { id }).pipe(
					map((res) => {
						const company = res.data.companies?.nodes?.[0];
						return company ? ({ id: company.id, name: company.name } as CompanyFilterItem) : null;
					})
				)
			)
		).pipe(map((companies) => companies.filter((company): company is CompanyFilterItem => !!company)));
	}
}
